import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Link, To } from 'react-router-dom';
import { useLocation } from 'react-router';
import { ALL_LANGUAGES } from '../constants';
import { useTranslation } from 'react-i18next';
import { DropDownMenu } from '../../../shared/common/DropDownMenu';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { AgeCategory } from '../../../../models/AgeCategory';

const StDropDown = styled(DropDownMenu).attrs({
    'data-displayname': 'LanguageDropDown'
})`
    font-size: 20px;
    && a {
        display: flex;
        font-size: 20px;
    }

    && .default-dd {
        top: 30px;
    }

    && .active-arrow {
        right: unset !important;
        left: 60px;
    }
`;

const StLanguageLink = styled(Link)`
    white-space: nowrap !important;
    font-size: 16px !important;
    padding: 4px 8px 4px 8px !important;
`;

type ItemTargetBuilderProps = {
    language: { readonly name: string };
    pathname: string;
    isMultiLanguageShopEnabled: boolean;
    ageCategorySearchParam: AgeCategory | undefined;
};

type Props = {
    readonly languages: ReadonlyArray<{ readonly name: string }>;
    readonly selectedLanguage: string;
    readonly label: string;
    readonly itemTargetBuilder: ({
        language,
        pathname,
        isMultiLanguageShopEnabled,
        ageCategorySearchParam
    }: ItemTargetBuilderProps) => To | null;
};

const LanguageDropDown: FC<Props> = props => {
    const { languages, selectedLanguage, label, itemTargetBuilder } = props;
    const [expanded, setExpanded] = useState(false);
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();
    const buildDestination = (language: { readonly name: string }) =>
        itemTargetBuilder({ language, pathname, isMultiLanguageShopEnabled, ageCategorySearchParam });

    return (
        <StDropDown
            expanded={expanded}
            onToggleClick={() => setExpanded(!expanded)}
            align="left"
            gap={2}
            onClickOutside={() => setExpanded(false)}
            label={label}
        >
            {languages
                .filter(language => language.name !== selectedLanguage)
                .map(language => {
                    const destination = buildDestination(language);
                    return (
                        <li key={language.name}>
                            <StLanguageLink to={destination!} onClick={() => setExpanded(false)}>
                                {language.name === ALL_LANGUAGES
                                    ? t('allLanguages')
                                    : t(`filters:language.${language.name}`, {
                                          defaultValue: language.name
                                      })}
                            </StLanguageLink>
                        </li>
                    );
                })}
        </StDropDown>
    );
};

export default LanguageDropDown;
